import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { resumeFullPdfHref, resumeQuickPdfHref } from "@/lib/resumePdfHref";

const BG_IMAGES = [
  `${import.meta.env.BASE_URL}kelly-bg-1.png`,
  `${import.meta.env.BASE_URL}kelly-bg-2.png`,
  `${import.meta.env.BASE_URL}kelly-bg-3.png`,
  `${import.meta.env.BASE_URL}kelly-bg-4.png`,
  `${import.meta.env.BASE_URL}kelly-bg-5.png`,
  `${import.meta.env.BASE_URL}kelly-bg-6.png`,
  `${import.meta.env.BASE_URL}kelly-bg-7.png`,
  `${import.meta.env.BASE_URL}kelly-bg-8.png`,
  `${import.meta.env.BASE_URL}kelly-bg-9.png`,
] as const;

/** Time each slide stays fully (or mostly) visible before crossfading to the next. */
const SLIDE_INTERVAL_MS = 5500;
/** Crossfade duration — keep subtle (CSS transition matches). */
const FADE_MS = 1800;

export default function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % BG_IMAGES.length);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Rotating background slideshow */}
      <div className="absolute inset-0 z-0">
        {BG_IMAGES.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 transition-opacity ease-in-out pointer-events-none"
            style={{
              opacity: i === activeIndex ? 1 : 0,
              transitionDuration: `${FADE_MS}ms`,
            }}
            aria-hidden
          >
            <img
              src={src}
              alt=""
              className="w-full h-full min-h-full object-cover object-center"
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
            />
          </div>
        ))}
        {/* Stronger left wash for headline + CTAs over bright photography */}
        <div
          className="absolute inset-0 z-[2] bg-gradient-to-r from-black/50 via-black/25 to-transparent pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute inset-0 z-[2] bg-gradient-to-r from-[#1E3A5F]/88 via-[#1E3A5F]/62 to-[#1E3A5F]/28 pointer-events-none"
          aria-hidden
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-20">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80 mb-6 drop-shadow-sm">
            Education Specialist · Special Education
          </p>
          <h1 className="font-['Crimson_Pro'] text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-md">
            Kelly Peterson
          </h1>
          <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-4 font-['Inter'] drop-shadow-sm">
            Education Specialist (Special Education)
          </p>
          <p className="text-base md:text-lg text-white/85 leading-relaxed mb-10 max-w-xl font-['Inter'] font-light drop-shadow-sm">
            26+ years TK–8 in inclusion, Special Day Class (SDC), and Resource Specialist Program (RSP) settings. Instruction stays aligned to each
            student&apos;s Individualized Education Program (IEP). Supports draw on Multi-Tiered System of Supports (MTSS) and Universal Design for
            Learning (UDL), with trust-centered mentorship that changes outcomes.
          </p>

          {/* CTA row: frosted panel for contrast on variable slides */}
          <div className="max-w-xl rounded-2xl border border-white/20 bg-white/[0.12] backdrop-blur-md shadow-[0_12px_40px_-12px_rgba(0,0,0,0.45)] p-4 sm:p-5">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-3">
                <a
                  href={resumeQuickPdfHref()}
                  download="Kelly_Peterson_Quick_Resume.pdf"
                  className="inline-flex min-h-11 justify-center items-center px-6 py-3.5 rounded-full text-sm font-semibold bg-white text-[#1E3A5F] hover:bg-white/95 transition-all duration-200 text-center cursor-pointer shadow-sm"
                >
                  Quick Resume (2 Pages)
                </a>
                <a
                  href={resumeFullPdfHref()}
                  download="Kelly_Peterson_Full_Professional_History.pdf"
                  className="inline-flex min-h-11 justify-center items-center px-6 py-3.5 rounded-full text-sm font-semibold border-2 border-white/90 text-white hover:bg-white/10 transition-all duration-200 text-center cursor-pointer"
                >
                  Full Professional History
                </a>
              </div>
              <Link
                to="/impact-stories"
                className="inline-flex min-h-10 justify-center items-center px-6 py-2.5 rounded-full text-sm font-medium text-white/90 border border-white/35 hover:bg-white/10 transition-all duration-200 text-center cursor-pointer"
              >
                View Impact Stories
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-px h-10 bg-white/40"></div>
        <i className="ri-arrow-down-line text-white/50 text-sm"></i>
      </div>
    </section>
  );
}
